import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useExotelSDK } from '../services/useExotelSDK'
import telephonyAudioService from '../services/telephonyAudioService'
import {
  updateAgentDeviceStatus,
  fetchContactsList,
  fetchCallerContext,
  quickCreateTask,
  quickCreateJob,
  createCallback,
  updateCallDisposition,
} from '../services/telephonyApi'
import InboundCallPopupDrawer from '../../../components/InboundCallPopupDrawer'
import PostCallTimesheetModal from '../components/PostCallTimesheetModal'
import { useNotificationBus } from '../../../context/notifications/AppNotificationBus'
import { NOTIFICATION_TYPES } from '../../../context/notifications/notificationTypes'
import { appToast } from '../../../components/AppToast'

const POPUP_CHANNEL = 'bridge_telephony_popup_channel'

const TelephonyContext = createContext(null)

const getCurrentUserId = () => {
  try {
    const user = JSON.parse(localStorage.getItem('user') || '{}')
    return user.id || user.user_id || 'default'
  } catch {
    return 'default'
  }
}

export function TelephonyProvider({ children }) {
  const navigate = useNavigate()
  const location = useLocation()

  // ── SIP Device Toggle State (ON = WebRTC Active / OFF = PSTN Fallback) ──────
  // User-scoped so switching accounts on the same browser does not leak toggle state
  const [isSipDeviceOn, setIsSipDeviceOn] = useState(() => {
    const uid = getCurrentUserId()
    const userVal = localStorage.getItem(`sip_device_on_${uid}`)
    if (userVal !== null) return userVal === 'true'
    return false // Default offline for safety unless explicitly enabled by this user
  })
  const [isSipToggling, setIsSipToggling] = useState(false)
  const [sipError, setSipError] = useState(null)

  // Synchronous ref to prevent stale closures inside SDK callbacks
  const isSipDeviceOnRef = useRef(isSipDeviceOn)
  useEffect(() => {
    isSipDeviceOnRef.current = isSipDeviceOn
  }, [isSipDeviceOn])

  // ── Universal User Gesture Priming (WhatsApp Web / Twilio Flex pattern) ─────
  // Modern browsers block autoplay unless the user has interacted with the document.
  // We attach a passive one-time capture listener on window for click/keydown/pointerdown.
  // The moment the agent touches the keyboard or clicks anywhere in the app, the audio
  // subsystem is pre-unlocked for all future incoming call events.
  const [isAudioPrimed, setIsAudioPrimed] = useState(() => telephonyAudioService.isPrimed())

  useEffect(() => {
    if (isAudioPrimed) return

    const handleFirstInteraction = () => {
      telephonyAudioService.primeAudio()
      setIsAudioPrimed(true)
      window.removeEventListener('click', handleFirstInteraction, true)
      window.removeEventListener('keydown', handleFirstInteraction, true)
      window.removeEventListener('pointerdown', handleFirstInteraction, true)
    }

    window.addEventListener('click', handleFirstInteraction, true)
    window.addEventListener('keydown', handleFirstInteraction, true)
    window.addEventListener('pointerdown', handleFirstInteraction, true)

    return () => {
      window.removeEventListener('click', handleFirstInteraction, true)
      window.removeEventListener('keydown', handleFirstInteraction, true)
      window.removeEventListener('pointerdown', handleFirstInteraction, true)
    }
  }, [isAudioPrimed])

  const testRingtone = useCallback(() => {
    telephonyAudioService.testRingtone()
    setIsAudioPrimed(true)
  }, [])

  // ── Active Call State ──────────────────────────────────────────────────────
  const [isCallActive, setIsCallActive] = useState(false)
  const [callState, setCallState] = useState('IDLE') // 'IDLE' | 'RINGING' | 'CONNECTING' | 'CONNECTED' | 'MISSED'
  const [isMuted, setIsMuted] = useState(false)
  const [isOnHold, setIsOnHold] = useState(false)
  const [activeCallerInfo, setActiveCallerInfo] = useState(null)
  const [callDurationSecs, setCallDurationSecs] = useState(0)
  const callTimerRef = useRef(null)

  // ── Post-Call Timesheet Modal State (Gap 5) ───────────────────────────────
  const [postCallModal, setPostCallModal] = useState(null) // { timesheetId, callSid, projectName, durationSeconds, callerName, callerNumber, direction, initialNotes }
  const [activeCallNotes, setActiveCallNotes] = useState('')
  const activeCallNotesRef = useRef('')

  // Synchronous ref to prevent stale closures during rapid telephony transitions
  const callStateRef = useRef('IDLE')
  useEffect(() => {
    callStateRef.current = callState
  }, [callState])

  // ── Popup Drawer / Dock Bar Toggle State ──────────────────────────────────
  const [isPopupOpen, setIsPopupOpen] = useState(false)
  const [isDockedToHeader, setIsDockedToHeader] = useState(false)

  // ── Desktop Notification + Global Popup Bridge ────────────────────────────────────
  const { push: pushNotification } = useNotificationBus()

  // ── Detached Mini Window Management ─────────────────────────────────────────
  const popupWindowRef = useRef(null)
  const broadcastChannelRef = useRef(null)

  // Open or focus the detached mini call window
  const openDetachedWindow = useCallback((state, info = {}) => {
    const params = new URLSearchParams({
      state:     state,
      name:      info.callerName   || 'Customer',
      number:    info.callerNumber || '',
      direction: info.direction    || 'inbound',
      project:   info.projectName  || '',
    }).toString()

    // Reuse existing window if still open
    if (popupWindowRef.current && !popupWindowRef.current.closed) {
      popupWindowRef.current.focus()
      return
    }

    const width  = 380
    const height = 560
    const left   = Math.max(0, (screen.availWidth  || 1280) - width  - 20)
    const top    = Math.max(0, (screen.availHeight || 720)  - height - 40)

    const features = [
      `width=${width}`, `height=${height}`, `left=${left}`, `top=${top}`,
      'resizable=no', 'scrollbars=no', 'status=no', 'menubar=no', 'toolbar=no',
    ].join(',')

    const win = window.open(`/loop/call-popup?${params}`, 'BridgeCallPopup', features)

    if (!win) {
      console.warn('[TelephonyContext] Pop-up was blocked. Falling back to in-page GlobalCallPopup only.')
      return
    }
    popupWindowRef.current = win
  }, [])

  // Close the detached window programmatically
  const closeDetachedWindow = useCallback(() => {
    if (broadcastChannelRef.current) {
      broadcastChannelRef.current.postMessage({ type: 'CLOSE_WINDOW' })
    }
    if (popupWindowRef.current && !popupWindowRef.current.closed) {
      setTimeout(() => {
        try { popupWindowRef.current?.close() } catch (_) {}
      }, 400)
    }
  }, [])

  // Broadcast state updates to the detached window
  const broadcastToPopup = useCallback((msg) => {
    if (broadcastChannelRef.current) {
      broadcastChannelRef.current.postMessage(msg)
    }
  }, [])

  // Setup BroadcastChannel on mount, listen for actions from mini window
  useEffect(() => {
    const channel = new BroadcastChannel(POPUP_CHANNEL)
    broadcastChannelRef.current = channel

    channel.onmessage = (event) => {
      const msg = event.data || {}
      if (msg.type === 'POPUP_MOUNTED') {
        // Handshake: mini window opened and is asking for current state
        channel.postMessage({
          type: 'SYNC_STATE',
          payload: {
            callState:   callStateRef.current,
            callerName:  activeCallerInfoRef.current?.contact_name || activeCallerInfoRef.current?.customer_name || activeCallerInfoRef.current?.from || 'Customer',
            callerNumber: activeCallerInfoRef.current?.from || '',
            direction:   activeCallerInfoRef.current?.direction || 'inbound',
            projectName: activeCallerInfoRef.current?.project_name || '',
            durationSecs: 0,
            isMuted: false,
            isOnHold: false,
          }
        })
      } else if (msg.type === 'CALL_ACTION') {
        const action = msg.action
        if (action === 'answer')          window.dispatchEvent(new CustomEvent('telephony:answer-call'))
        if (action === 'decline')         window.dispatchEvent(new CustomEvent('telephony:decline-call'))
        if (action === 'end')             window.dispatchEvent(new CustomEvent('telephony:end-call'))
        if (action === 'cancel_outbound') window.dispatchEvent(new CustomEvent('telephony:cancel-outbound-call'))
        if (action === 'toggle_mute')     window.dispatchEvent(new CustomEvent('telephony:toggle-mute'))
        if (action === 'toggle_hold')     window.dispatchEvent(new CustomEvent('telephony:toggle-hold'))
      }
    }

    return () => {
      channel.close()
      broadcastChannelRef.current = null
    }
  }, [])

  // ── SDK Callbacks ─────────────────────────────────────────────────────────
  const sdk = useExotelSDK({
    onRegistered: useCallback(() => {
      console.log('[TelephonyContext] SIP device registered')
      setSipError(null)
      // Only mark online in backend if SIP toggle switch is actively ON
      if (isSipDeviceOnRef.current) {
        updateAgentDeviceStatus(true).catch((e) => console.warn('Failed to sync online status:', e))
      }
    }, []),
    onRegistrationFailed: useCallback((err) => {
      console.error('[TelephonyContext] SIP registration failed:', err)
      setSipError('SIP registration failed. Check your network or credentials.')
      setIsSipDeviceOn(false)
      const uid = getCurrentUserId()
      localStorage.setItem(`sip_device_on_${uid}`, 'false')
      localStorage.setItem('sip_device_on', 'false')
    }, []),
    onIncomingCall: useCallback((callInfo) => {
      console.log('[TelephonyContext] Incoming call received:', callInfo)
      callStateRef.current = 'RINGING'
      setActiveCallerInfo({ ...callInfo, direction: 'inbound' })
      setIsCallActive(true)
      setCallState('RINGING')
      setIsPopupOpen(true)
      setIsDockedToHeader(false)
      setCallDurationSecs(0)

      // Dispatch to GlobalCallPopup (in-page floating card) + open detached mini window
      const inboundName   = callInfo?.customer_name || callInfo?.from || 'Unknown'
      const inboundNumber = callInfo?.from || ''
      window.dispatchEvent(new CustomEvent('telephony:incoming-call', {
        detail: { callerName: inboundName, callerNumber: inboundNumber, callSid: callInfo?.call_sid, direction: 'inbound' }
      }))
      // Start ringtone — dual-engine (HTML5 Audio + Web Audio API fallback)
      telephonyAudioService.startRingtone()
      openDetachedWindow('RINGING', { callerName: inboundName, callerNumber: inboundNumber, direction: 'inbound' })

      // Gap 4: Fetch rich caller context (open tickets, tasks, last interaction)
      const callerNumber = callInfo?.from || ''
      if (callerNumber) {
        fetchCallerContext(callerNumber)
          .then((res) => {
            const ctx = res?.data
            if (ctx && (ctx.known_caller || ctx.contact_name || ctx.customer_name)) {
              const resolvedName = ctx.contact_name || ctx.contact_person || ctx.customer_name || inboundName
              const resolvedProject = ctx.project_name || ''
              setActiveCallerInfo((prev) => ({
                ...prev,
                customer_name: resolvedName,
                contact_name: ctx.contact_name || resolvedName,
                contact_person: ctx.contact_person || resolvedName,
                project_name: resolvedProject,
                project_id: ctx.project_id || prev?.project_id || null,
                known_caller: ctx.known_caller !== false,
                open_tickets_count: ctx.open_tickets_count ?? 0,
                open_tasks_count: ctx.open_tasks_count ?? 0,
                last_interaction: ctx.last_interaction || null,
              }))

              // 1. Broadcast resolved contact and project name to Detached Mini Window
              broadcastToPopup({
                type: 'UPDATE_CALLER_INFO',
                payload: {
                  callerName: resolvedName,
                  projectName: resolvedProject,
                  callerNumber: callerNumber,
                }
              })

              // 2. Dispatch update event to in-page GlobalCallPopup floating card
              window.dispatchEvent(new CustomEvent('telephony:caller-info-updated', {
                detail: {
                  callerName: resolvedName,
                  projectName: resolvedProject,
                  callerNumber: callerNumber,
                }
              }))
            } else {
              // Fallback: try contacts list
              return fetchContactsList(callerNumber).then((r) => {
                const contacts = r?.data || r || []
                if (Array.isArray(contacts) && contacts.length > 0) {
                  const match = contacts[0]
                  const matchedName = match.contact_name || match.contact_person || match.name || 'Customer'
                  const matchedProject = match.project_name || ''
                  setActiveCallerInfo((prev) => ({
                    ...prev,
                    customer_name: matchedName,
                    contact_name: matchedName,
                    contact_person: matchedName,
                    project_name: matchedProject,
                  }))

                  broadcastToPopup({
                    type: 'UPDATE_CALLER_INFO',
                    payload: {
                      callerName: matchedName,
                      projectName: matchedProject,
                      callerNumber: callerNumber,
                    }
                  })

                  window.dispatchEvent(new CustomEvent('telephony:caller-info-updated', {
                    detail: {
                      callerName: matchedName,
                      projectName: matchedProject,
                      callerNumber: callerNumber,
                    }
                  }))
                }
              })
            }
          })
          .catch((e) => {
            console.warn('[TelephonyContext] Caller context lookup error:', e)
          })
      }
    }, [openDetachedWindow]),
    onCallConnected: useCallback((callInfo) => {
      console.log('[TelephonyContext] Call connected successfully')
      callStateRef.current = 'CONNECTED'
      setCallState('CONNECTED')
      setCallDurationSecs(0)
      if (callTimerRef.current) clearInterval(callTimerRef.current)
      callTimerRef.current = setInterval(() => {
        setCallDurationSecs((s) => {
          const next = s + 1
          // Broadcast live timer tick to detached window
          if (broadcastChannelRef.current) {
            broadcastChannelRef.current.postMessage({ type: 'TIMER_TICK', durationSecs: next })
          }
          return next
        })
      }, 1000)
      // Notify GlobalCallPopup + sync detached window to connected state
      // Stop ringtone the moment call is answered / connected
      telephonyAudioService.stopRingtone()
      window.dispatchEvent(new CustomEvent('telephony:call-connected', {
        detail: { callerInfo: activeCallerInfoRef.current }
      }))
      if (broadcastChannelRef.current) {
        broadcastChannelRef.current.postMessage({ type: 'CALL_CONNECTED' })
      }
    }, []),
    onCallEnded: useCallback(({ wasOutbound, callData } = {}) => {
      console.log('[TelephonyContext] Call ended callback. Current state:', callStateRef.current)
      if (callTimerRef.current) {
        clearInterval(callTimerRef.current)
        callTimerRef.current = null
      }

      const prev = callStateRef.current
      // Capture caller info before clearing state
      const endedCallerInfo = activeCallerInfoRef.current
      const endedDuration = callDurationSecsRef.current

      // Notify detached window that call ended (it will auto-close after transition)
      // Always stop ringtone on call end (covers missed, declined, hung-up cases)
      telephonyAudioService.stopRingtone()
      if (broadcastChannelRef.current) {
        broadcastChannelRef.current.postMessage(
          prev === 'RINGING' ? { type: 'CALL_MISSED' } : { type: 'CALL_ENDED' }
        )
      }
      setTimeout(() => closeDetachedWindow(), 1600)

      // Gating for Post-Call Timesheet Modal:
      // STRICT REQUIREMENT: Only show the post-call modal if the call was actually CONNECTED and talk time occurred (>0s).
      // If the call was ringing, missed, declined, or cancelled by the caller before answer, NEVER show the modal!
      const wasConnected = prev === 'CONNECTED' || (prev !== 'RINGING' && prev !== 'DECLINED' && prev !== 'MISSED' && endedDuration > 0)

      if (prev === 'RINGING' || prev === 'MISSED') {
        console.warn('[TelephonyContext] Inbound call missed or hunt routed to next agent')
        setCallState('MISSED')
        // Notify GlobalCallPopup to show 3s missed flash
        window.dispatchEvent(new CustomEvent('telephony:call-missed', {
          detail: { callerInfo: endedCallerInfo }
        }))
        // Push to notification bus (future bell / history panel)
        pushNotification({
          type: NOTIFICATION_TYPES.CALL_MISSED,
          title: 'Missed Call',
          body: endedCallerInfo?.customer_name
            ? `${endedCallerInfo.customer_name} (${endedCallerInfo.from || ''})`
            : endedCallerInfo?.from || 'Unknown',
        })
        setTimeout(() => {
          setIsCallActive(false)
          setCallState('IDLE')
          setIsPopupOpen(false)
          setIsDockedToHeader(false)
          setActiveCallerInfo(null)
          setCallDurationSecs(0)
        }, 3000)
      } else if (prev === 'DECLINED') {
        console.log('[TelephonyContext] Inbound call was explicitly declined by agent')
        window.dispatchEvent(new CustomEvent('telephony:call-ended', { detail: { callerInfo: endedCallerInfo } }))
        setIsCallActive(false)
        setCallState('IDLE')
        setIsPopupOpen(false)
        setIsDockedToHeader(false)
        setActiveCallerInfo(null)
        setCallDurationSecs(0)
      } else if (wasConnected && endedDuration > 0) {
        // Gap 5: Show post-call timesheet confirmation modal ONLY for connected calls with talk duration
        window.dispatchEvent(new CustomEvent('telephony:call-ended', { detail: { callerInfo: endedCallerInfo } }))
        const timesheetId = callData?.timesheet_id || endedCallerInfo?.timesheet_id || null
        const currentCallNotes = activeCallNotesRef.current || ''
        setPostCallModal({
          timesheetId,
          callSid: callData?.call_sid || endedCallerInfo?.call_sid || null,
          projectName: endedCallerInfo?.project_name || null,
          durationSeconds: endedDuration,
          callerName: endedCallerInfo?.customer_name || endedCallerInfo?.contact_name || null,
          callerNumber: endedCallerInfo?.from || endedCallerInfo?.caller_number || null,
          direction: endedCallerInfo?.direction || 'inbound',
          initialNotes: currentCallNotes,
        })
        setActiveCallNotes('')
        activeCallNotesRef.current = ''

        setIsCallActive(false)
        setCallState('IDLE')
        setIsPopupOpen(false)
        setIsDockedToHeader(false)
        setActiveCallerInfo(null)
        setCallDurationSecs(0)
      } else {
        // Non-connected, dropped, or 0 duration call
        window.dispatchEvent(new CustomEvent('telephony:call-ended', { detail: { callerInfo: endedCallerInfo } }))
        setIsCallActive(false)
        setCallState('IDLE')
        setIsPopupOpen(false)
        setIsDockedToHeader(false)
        setActiveCallerInfo(null)
        setCallDurationSecs(0)
      }
      setIsMuted(false)
      setIsOnHold(false)
    }, [pushNotification, closeDetachedWindow]),
    onError: useCallback((err) => {
      console.error('[TelephonyContext] SDK error:', err)
      setSipError('Telephony error: ' + (err?.message || String(err)))
      if (callStateRef.current === 'RINGING') {
        setCallState('MISSED')
        setTimeout(() => {
          setIsCallActive(false)
          setCallState('IDLE')
          setIsPopupOpen(false)
          setIsDockedToHeader(false)
          setActiveCallerInfo(null)
        }, 4000)
      } else {
        setIsCallActive(false)
        setCallState('IDLE')
        setIsPopupOpen(false)
        setIsDockedToHeader(false)
        setActiveCallerInfo(null)
      }
    }, []),
  })

  const sdkRef = useRef(sdk)
  useEffect(() => {
    sdkRef.current = sdk
  }, [sdk])

  // Manage SIP device lifecycle
  useEffect(() => {
    if (isSipDeviceOn) {
      sdkRef.current?.initialize()
    } else {
      sdkRef.current?.destroy()
    }
  }, [isSipDeviceOn])

  // Refs to capture current values in stale closures (needed for onCallEnded Gap 5)
  const activeCallerInfoRef = useRef(null)
  const callDurationSecsRef = useRef(0)
  useEffect(() => { activeCallerInfoRef.current = activeCallerInfo }, [activeCallerInfo])
  useEffect(() => { callDurationSecsRef.current = callDurationSecs }, [callDurationSecs])

  // Clean up SDK on window unload
  useEffect(() => {
    const handleBeforeUnload = () => {
      if (isSipDeviceOnRef.current) {
        sdkRef.current?.destroy()
      }
    }
    window.addEventListener('beforeunload', handleBeforeUnload)
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload)
    }
  }, [])

  // ── Turn off SIP Device & Teardown WebRTC (Used on Logout / Session Expiry) ──
  const turnOffSipDevice = useCallback(async () => {
    try {
      await updateAgentDeviceStatus(false)
    } catch (e) {
      console.warn('[TelephonyContext] Failed to update agent device status on logout:', e)
    }
    setIsSipDeviceOn(false)
    const uid = getCurrentUserId()
    localStorage.setItem(`sip_device_on_${uid}`, 'false')
    localStorage.setItem('sip_device_on', 'false')
    try {
      sdkRef.current?.destroy()
    } catch (e) {
      console.warn('[TelephonyContext] Failed to destroy SDK on logout:', e)
    }
    window.dispatchEvent(new CustomEvent('telephony:presence-changed', {
      detail: { isOnline: false, userId: uid }
    }))
  }, [])

  // Listen to global logout and auth-expired events to immediately kill SIP device
  useEffect(() => {
    const handleLogoutEvent = () => {
      console.log('[TelephonyContext] Received logout/session expiry event - turning off SIP device')
      turnOffSipDevice()
    }
    window.addEventListener('auth:logout', handleLogoutEvent)
    window.addEventListener('auth-expired', handleLogoutEvent)
    return () => {
      window.removeEventListener('auth:logout', handleLogoutEvent)
      window.removeEventListener('auth-expired', handleLogoutEvent)
    }
  }, [turnOffSipDevice])

  // Clean up SDK on provider unmount (e.g. navigating out to /login)
  useEffect(() => {
    return () => {
      try {
        sdkRef.current?.destroy()
      } catch (_) {}
    }
  }, [])

  // ── SIP Toggle Action ──────────────────────────────────────────────────────
  const handleSipToggle = useCallback(async () => {
    const nextState = !isSipDeviceOn
    setIsSipToggling(true)
    setSipError(null)
    try {
      await updateAgentDeviceStatus(nextState)
      setIsSipDeviceOn(nextState)
      const uid = getCurrentUserId()
      localStorage.setItem(`sip_device_on_${uid}`, String(nextState))
      localStorage.setItem('sip_device_on', String(nextState))
      if (nextState) {
        // Prime audio context on the user gesture of turning on SIP device
        // This pre-unlocks browser autoplay policy BEFORE the first call arrives
        telephonyAudioService.primeAudio()
      } else {
        sdkRef.current?.destroy()
        // Stop any lingering ringtone if SIP is disabled mid-ring
        telephonyAudioService.stopRingtone()
      }
      // Broadcast presence change event so ManagerQueueMonitorCard updates instantly
      window.dispatchEvent(new CustomEvent('telephony:presence-changed', {
        detail: { isOnline: nextState, userId: uid }
      }))
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || 'Failed to update SIP status'
      setSipError(typeof msg === 'string' ? msg : JSON.stringify(msg))
    } finally {
      setIsSipToggling(false)
    }
  }, [isSipDeviceOn])

  // ── Initiate Outbound Call ─────────────────────────────────────────────────
  const makeCall = useCallback((numberToCall, contactInfo = {}) => {
    if (!numberToCall) {
      appToast.warning('Please enter or select a phone number first.')
      return false
    }
    if (!isSipDeviceOn) {
      appToast.warning('SIP Device is OFF. Please turn ON the SIP Device toggle switch to place calls.')
      return false
    }

    const outboundName = contactInfo.name || contactInfo.label || contactInfo.customer_name || 'Customer'

    setActiveCallerInfo({
      from: numberToCall,
      direction: 'outbound',
      customer_name: outboundName,
      project_name: contactInfo.project_name || null,
      contact_person: contactInfo.contact_person || null,
    })
    setIsCallActive(true)
    setCallState('RINGING')
    setIsPopupOpen(true)
    setIsDockedToHeader(false)
    setCallDurationSecs(0)

    // Notify GlobalCallPopup (floating card) + open detached mini window
    window.dispatchEvent(new CustomEvent('telephony:outbound-call-started', {
      detail: { callerName: outboundName, callerNumber: numberToCall, direction: 'outbound' }
    }))
    openDetachedWindow('OUTBOUND_CALLING', { callerName: outboundName, callerNumber: numberToCall, direction: 'outbound' })

    // Lookup caller/client context from ProjectContactNumbers for outbound call
    fetchCallerContext(numberToCall)
      .then((res) => {
        const ctx = res?.data
        if (ctx && (ctx.known_caller || ctx.contact_name || ctx.customer_name)) {
          const resolvedName = ctx.contact_name || ctx.contact_person || ctx.customer_name || outboundName
          const resolvedProject = ctx.project_name || contactInfo.project_name || ''
          setActiveCallerInfo((prev) => ({
            ...prev,
            customer_name: resolvedName,
            contact_name: ctx.contact_name || resolvedName,
            contact_person: ctx.contact_person || resolvedName,
            project_name: resolvedProject,
            project_id: ctx.project_id || prev?.project_id || null,
            known_caller: true,
            open_tickets_count: ctx.open_tickets_count ?? 0,
            open_tasks_count: ctx.open_tasks_count ?? 0,
            last_interaction: ctx.last_interaction || null,
          }))

          broadcastToPopup({
            type: 'UPDATE_CALLER_INFO',
            payload: {
              callerName: resolvedName,
              projectName: resolvedProject,
              callerNumber: numberToCall,
            }
          })

          window.dispatchEvent(new CustomEvent('telephony:caller-info-updated', {
            detail: {
              callerName: resolvedName,
              projectName: resolvedProject,
              callerNumber: numberToCall,
            }
          }))
        }
      })
      .catch((e) => {
        console.warn('[TelephonyContext] Outbound caller context lookup error:', e)
      })

    const success = sdk.makeCall(numberToCall)
    if (!success) {
      setCallState('IDLE')
      setIsCallActive(false)
      setIsPopupOpen(false)
      window.dispatchEvent(new CustomEvent('telephony:call-ended', {}))
      closeDetachedWindow()
    }
    return success
  }, [isSipDeviceOn, sdk, openDetachedWindow, closeDetachedWindow])

  const answerCall = useCallback(() => {
    console.log('[TelephonyContext] Answering incoming call...')
    // Stop ringtone immediately when agent clicks Answer
    telephonyAudioService.stopRingtone()
    callStateRef.current = 'CONNECTING'
    setCallState('CONNECTING')
    sdk.answerCall()
  }, [sdk])

  const declineCall = useCallback(() => {
    console.log('[TelephonyContext] Declining incoming call...')
    // Stop ringtone immediately when agent declines
    telephonyAudioService.stopRingtone()
    const sid = activeCallerInfoRef.current?.call_sid
    if (sid) {
      updateCallDisposition(sid, 'busy', 'agent_declined').catch((err) => {
        console.warn('[TelephonyContext] Failed to report call decline disposition:', err)
      })
    }
    callStateRef.current = 'DECLINED'
    sdk.declineCall()
    setIsCallActive(false)
    setCallState('IDLE')
    setIsPopupOpen(false)
    setIsDockedToHeader(false)
    setActiveCallerInfo(null)
  }, [sdk])

  const endCall = useCallback(() => {
    console.log('[TelephonyContext] Ending active call...')
    callStateRef.current = 'IDLE'
    sdk.endCall()
    if (callTimerRef.current) {
      clearInterval(callTimerRef.current)
      callTimerRef.current = null
    }
    setIsCallActive(false)
    setCallState('IDLE')
    setIsPopupOpen(false)
    setIsDockedToHeader(false)
    setActiveCallerInfo(null)
    setCallDurationSecs(0)
  }, [sdk])

  const toggleMute = useCallback(() => {
    const next = !isMuted
    setIsMuted(next)
    sdk.setMute(next)
    broadcastToPopup({ type: 'SYNC_STATE', payload: { isMuted: next } })
  }, [isMuted, sdk, broadcastToPopup])

  const toggleHold = useCallback(() => {
    const next = !isOnHold
    setIsOnHold(next)
    sdk.setHold(next)
    broadcastToPopup({ type: 'SYNC_STATE', payload: { isOnHold: next } })
  }, [isOnHold, sdk, broadcastToPopup])

  // ── GlobalCallPopup → TelephonyContext action bridge ─────────────────────
  // The floating popup dispatches these events; we listen and invoke the real SDK methods.
  useEffect(() => {
    const onAnswer  = () => answerCall()
    const onDecline = () => declineCall()
    const onEnd     = () => endCall()
    const onCancelOutbound = () => endCall()
    const onMute    = () => toggleMute()
    const onHold    = () => toggleHold()

    window.addEventListener('telephony:answer-call',         onAnswer)
    window.addEventListener('telephony:decline-call',        onDecline)
    window.addEventListener('telephony:end-call',            onEnd)
    window.addEventListener('telephony:cancel-outbound-call', onCancelOutbound)
    window.addEventListener('telephony:toggle-mute',         onMute)
    window.addEventListener('telephony:toggle-hold',         onHold)

    return () => {
      window.removeEventListener('telephony:answer-call',         onAnswer)
      window.removeEventListener('telephony:decline-call',        onDecline)
      window.removeEventListener('telephony:end-call',            onEnd)
      window.removeEventListener('telephony:cancel-outbound-call', onCancelOutbound)
      window.removeEventListener('telephony:toggle-mute',         onMute)
      window.removeEventListener('telephony:toggle-hold',         onHold)
    }
  }, [answerCall, declineCall, endCall, toggleMute, toggleHold])

  const minimizeToDock = useCallback(() => {
    setIsPopupOpen(false)
    setIsDockedToHeader(true)
  }, [])

  const expandFromDock = useCallback(() => {
    setIsDockedToHeader(false)
    setIsPopupOpen(true)
  }, [])

  // ── Quick Actions for Drawer (Gap 4 — real API) ───────────────────────────
  const handleQuickCreateTask = useCallback(async (taskPayload) => {
    const projectId = activeCallerInfo?.project_id
    if (!projectId) {
      // Fall back to navigation if no project identified
      minimizeToDock()
      navigate('/nerve/task/entry', {
        state: {
          prefillCustomer: activeCallerInfo?.customer_name,
          prefillProject: activeCallerInfo?.project_name,
          prefillPhone: activeCallerInfo?.from,
        },
      })
      return
    }
    try {
      const res = await quickCreateTask({
        project_id: projectId,
        task_name: taskPayload?.task_name || 'Follow-up Task',
        priority: taskPayload?.priority || 'MEDIUM',
        description: `Created from active call with ${activeCallerInfo?.customer_name || activeCallerInfo?.from}`,
      })
      console.log('[TelephonyContext] Task created:', res?.data)
    } catch (err) {
      console.error('[TelephonyContext] Quick create task failed:', err)
    }
  }, [minimizeToDock, navigate, activeCallerInfo])

  const handleQuickCreateJob = useCallback(async (jobPayload) => {
    const projectId = activeCallerInfo?.project_id
    if (!projectId) {
      minimizeToDock()
      navigate('/nerve/job/entry', {
        state: {
          prefillCustomer: activeCallerInfo?.customer_name,
          prefillProject: activeCallerInfo?.project_name,
          prefillPhone: activeCallerInfo?.from,
        },
      })
      return
    }
    try {
      const res = await quickCreateJob({
        task_id: jobPayload?.task_id,
        job_name: jobPayload?.job_name || 'Follow-up Job',
        planned_hours: 0.5,
      })
      console.log('[TelephonyContext] Job created:', res?.data)
    } catch (err) {
      console.error('[TelephonyContext] Quick create job failed:', err)
    }
  }, [minimizeToDock, navigate, activeCallerInfo])

  const handleScheduleCallback = useCallback(async () => {
    const contactNumber = activeCallerInfo?.from || activeCallerInfo?.caller_number || ''
    const d = new Date(Date.now() + 3600000) // default: 1 hour from now in local time
    const pad = (n) => String(n).padStart(2, '0')
    const scheduledAt = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`
    try {
      await createCallback({
        contact_name: contactName,
        contact_number: contactNumber,
        project_id: activeCallerInfo?.project_id || null,
        call_sid: activeCallerInfo?.call_sid || null,
        scheduled_at: scheduledAt,
        notes: `Callback scheduled during active call with ${contactName}`,
      })
      console.log('[TelephonyContext] Callback scheduled for', contactName)
    } catch (err) {
      console.error('[TelephonyContext] Schedule callback failed:', err)
    }
  }, [activeCallerInfo])

  const handleSaveNotes = useCallback((notes) => {
    setActiveCallNotes(notes || '')
    activeCallNotesRef.current = notes || ''
  }, [])

  const contextValue = {
    isSipDeviceOn,
    isSipToggling,
    sipError,
    handleSipToggle,
    isCallActive,
    callState,
    activeCallerInfo,
    callDurationSecs,
    isMuted,
    isOnHold,
    isPopupOpen,
    isDockedToHeader,
    makeCall,
    answerCall,
    declineCall,
    endCall,
    toggleMute,
    toggleHold,
    minimizeToDock,
    expandFromDock,
    setIsPopupOpen,
    turnOffSipDevice,
    isAudioPrimed,
    testRingtone,
  }

  return (
    <TelephonyContext.Provider value={contextValue}>
      {children}

      {/* Global Inbound & Outbound Call Drawer Popup */}
      <InboundCallPopupDrawer
        isOpen={isPopupOpen}
        callState={callState}
        direction={activeCallerInfo?.direction || 'inbound'}
        callerNumber={activeCallerInfo?.from || ''}
        customerName={activeCallerInfo?.customer_name || 'Customer'}
        projectName={activeCallerInfo?.project_name}
        callerInfo={activeCallerInfo}
        isKnownCaller={activeCallerInfo?.known_caller !== false}
        openTicketsCount={activeCallerInfo?.open_tickets_count ?? 0}
        openTasksCount={activeCallerInfo?.open_tasks_count ?? 0}
        lastInteraction={activeCallerInfo?.last_interaction || null}
        callDurationSecs={callDurationSecs}
        isMuted={isMuted}
        isOnHold={isOnHold}
        onAnswerCall={answerCall}
        onDeclineCall={endCall}
        onAnswer={answerCall}
        onReject={declineCall}
        onHangup={endCall}
        onToggleMute={toggleMute}
        onToggleHold={toggleHold}
        onMinimizeToDock={minimizeToDock}
        onMinimizeToHeader={minimizeToDock}
        notes={activeCallNotes}
        onSaveNotes={handleSaveNotes}
        onQuickCreateTask={handleQuickCreateTask}
        onQuickCreateJob={handleQuickCreateJob}
        onScheduleCallback={handleScheduleCallback}
      />

      {/* Gap 5: Post-Call Timesheet Confirmation Modal */}
      {postCallModal && (
        <PostCallTimesheetModal
          isOpen={!!postCallModal}
          onClose={() => setPostCallModal(null)}
          timesheetId={postCallModal.timesheetId}
          callSid={postCallModal.callSid}
          projectName={postCallModal.projectName}
          durationSeconds={postCallModal.durationSeconds}
          callerName={postCallModal.callerName}
          callerNumber={postCallModal.callerNumber}
          direction={postCallModal.direction}
          initialNotes={postCallModal.initialNotes}
        />
      )}
    </TelephonyContext.Provider>
  )
}

export function useTelephony() {
  const ctx = useContext(TelephonyContext)
  if (!ctx) {
    throw new Error('useTelephony must be used within a TelephonyProvider')
  }
  return ctx
}
